import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Landmark, Link2Off, TriangleAlert } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AdvertisersApi, type Advertiser } from '@/services/advertisers/advertisersApi'
import { apiErrorDescription } from '@/services/core/apiError'

interface AdvertiserPoliticalLinkDialogProps {
  open: boolean
  onClose: () => void
  advertiser: Advertiser
}

/**
 * Shows the political campaigns an advertiser is linked to, with a way to
 * unlink each. Saving the advertiser with a shorter campaign list removes the
 * link; the campaign itself is left untouched.
 */
export function AdvertiserPoliticalLinkDialog({ open, onClose, advertiser }: AdvertiserPoliticalLinkDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  // Campaigns without a publicId are left out of politicalCampaigns, so list by id and name what we can.
  const names = new Map(advertiser.politicalCampaigns.map((c) => [c.id, c.name]))

  const unlinkMutation = useMutation({
    mutationFn: (campaignId: string) =>
      AdvertisersApi.update(advertiser.id, {
        name: advertiser.name,
        politicalCampaigns: advertiser.politicalCampaignIds
          .filter((id) => id !== campaignId)
          .map((id) => ({ id })),
      }),
    onSuccess: (data) => {
      toast.success(t('advertisers.politicalUnlinked'), { duration: 3000 })
      queryClient.setQueryData(['advertiser', advertiser.id], data)
      queryClient.invalidateQueries({ queryKey: ['advertisers'] })
      queryClient.invalidateQueries({ queryKey: ['election-topics', advertiser.id] })
      if (data.politicalCampaignIds.length === 0) handleClose()
    },
  })

  const handleClose = () => {
    unlinkMutation.reset()
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Landmark className="inline size-4 mr-2" />
            {t('advertisers.politicalLinkedTitle')}
          </DialogTitle>
          <DialogDescription>{t('advertisers.politicalLinkedDescription', { name: advertiser.name })}</DialogDescription>
        </DialogHeader>

        <ul className="flex flex-col divide-y rounded-md border">
          {advertiser.politicalCampaignIds.map((campaignId) => (
            <li key={campaignId} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="min-w-0 truncate text-sm">{names.get(campaignId) || campaignId}</span>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="shrink-0"
                loading={unlinkMutation.isPending && unlinkMutation.variables === campaignId}
                disabled={unlinkMutation.isPending}
                onClick={() => unlinkMutation.mutate(campaignId)}
              >
                <Link2Off className="size-4" />
                {t('advertisers.politicalUnlink')}
              </Button>
            </li>
          ))}
        </ul>

        {unlinkMutation.error && (
          <Alert variant="destructive">
            <TriangleAlert className="size-4" />
            <AlertDescription>{apiErrorDescription(unlinkMutation.error)}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={handleClose} disabled={unlinkMutation.isPending}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
